
import { API_BASE_URL } from '../config';
import { MOCK_ROSTER } from '../mockData';
import { UserRole, Lead, RosterMember, Asset } from '../types'; 
import { loggingService } from './loggingService';
import { LogLevel, LogCategory } from '@/types/logs';

// --- Interfaces ---
interface Node {
  id: string;
  label: string;
  type: | 'Artist' | 'Venue' | 'Sponsor' | 'DAO Governor' | 'Service' | 'Treasury' | 'Lead';
}
interface Edge {
  source: string;
  target: string;
  label: | 'PLAYS_AT' | 'SPONSORED_BY' | 'USES_SERVICE' | 'OVERSEES' | 'CONNECTED_TO';
}

// --- Initial Data ---
const artist = MOCK_ROSTER.find((m) => m.role === UserRole.ARTIST);
const venue = MOCK_ROSTER.find((m) => m.role === UserRole.VENUE);
const sponsor = MOCK_ROSTER.find((m) => m.role === UserRole.SPONSOR);
const daoGovernor = MOCK_ROSTER.find((m) => m.role === UserRole.DAO_GOVERNOR);
export const INITIAL_NODES: Node[] = [
  ...(artist ? [{ id: artist.id, label: artist.name, type: 'Artist' as const }] : []),
  ...(venue ? [{ id: venue.id, label: venue.name, type: 'Venue' as const }] : []),
  ...(sponsor ? [{ id: sponsor.id, label: sponsor.name, type: 'Sponsor' as const }] : []),
  ...(daoGovernor ? [{ id: daoGovernor.id, label: daoGovernor.name, type: 'DAO Governor' as const, }] : []),
  { id: 'service_legal', label: 'Legal Eagle', type: 'Service' },
  { id: 'treasury_main', label: 'Main Treasury', type: 'Treasury' },
];
export const INITIAL_EDGES: Edge[] = [
  ...(artist && venue ? [{ source: artist.id, target: venue.id, label: 'PLAYS_AT' as const }] : []),
  ...(artist && sponsor ? [{ source: artist.id, target: sponsor.id, label: 'SPONSORED_BY' as const, }] : []),
  ...(artist ? [{ source: artist.id, target: 'service_legal', label: 'USES_SERVICE' as const, }] : []),
  ...(daoGovernor && artist ? [{ source: daoGovernor.id, target: artist.id, label: 'OVERSEES' as const, }] : []),
  ...(daoGovernor && venue ? [{ source: daoGovernor.id, target: venue.id, label: 'OVERSEES' as const }] : []),
  ...(daoGovernor && sponsor ? [{ source: daoGovernor.id, target: sponsor.id, label: 'OVERSEES' as const, }] : []),
  ...(daoGovernor ? [{ source: daoGovernor.id, target: 'treasury_main', label: 'CONNECTED_TO' as const, }] : []),
];

class KnowledgeGraph {
  private connections: Map<string, string[]>;
  public nodes: Node[];
  public edges: Edge[];

  constructor() {
    this.connections = new Map();
    this.nodes = [];
    this.edges = [];
  }

  public loadData(nodes: Node[], edges: Edge[]): void {
    this.nodes = nodes;
    this.edges = edges;
    this.connections.clear();
    edges.forEach((edge) => {
      if (!this.connections.has(edge.source)) {
        this.connections.set(edge.source, []);
      }
      this.connections.get(edge.source)!.push(edge.target);
    });
  }

  public addLead(lead: Lead): void {
    if (this.nodes.some(node => node.id === lead.id)) {
      loggingService.log('Attempted to add a duplicate lead to the knowledge graph', LogLevel.WARN, LogCategory.GRAPH, { leadId: lead.id });
      return;
    }
    const newNode: Node = { id: lead.id, label: lead.name, type: 'Lead' };
    this.nodes.push(newNode);
    loggingService.log('Added new lead to the knowledge graph', LogLevel.INFO, LogCategory.GRAPH, { leadId: lead.id, name: lead.name });
  }
  /**
   * Retrieves all members from the roster, ensuring that the asset data for each member is complete.
   * This function maps over the `MOCK_ROSTER` data and supplements it with default values for any missing asset fields,
   * preventing runtime errors when the data is accessed in components.
   *
   * @returns {RosterMember[]} An array of roster members, with guaranteed complete asset data.
   */
  public getRosterMembers(): RosterMember[] {
    return MOCK_ROSTER.map((member: RosterMember) => {
      // Create a new asset object with default values
      const assets: Asset = {
        ips: member.assets?.ips ?? [],
        nfts: member.assets?.nfts ?? [],
        events: member.assets?.events ?? [],
        services: member.assets?.services ?? [],
        productions: member.assets?.productions ?? [],
        contents: member.assets?.contents ?? 0,
        products: member.assets?.products ?? 0,
        equipment: member.assets?.equipment ?? 0,
        instruments: member.assets?.instruments ?? 0,
        tickets: member.assets?.tickets ?? 0,
      };

      // Return a new member object with the complete assets
      return {
        ...member,
        assets,
      };
    });
  }

  public async findLeads(query: string, userId: string): Promise<Lead[]> {
    loggingService.log('[GRAPH] Starting AI lead generation', LogLevel.INFO, LogCategory.GRAPH, { query, userId });

    try {
      // Use the centralized API_BASE_URL to call the backend
      const response = await fetch(`${API_BASE_URL}/api/ai/generate-leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: query }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})); // Handle non-JSON error responses
        const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const leads: Omit<Lead, 'status'>[] = await response.json();

      // Add the default 'New' status to each lead
      const leadsWithStatus: Lead[] = leads.map(lead => ({ ...lead, status: 'New' }));
      
      loggingService.log('[GRAPH] Successfully generated leads with AI', LogLevel.INFO, LogCategory.GRAPH, { count: leads.length, userId });
      return leadsWithStatus;

    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      loggingService.log('[GRAPH] AI lead generation service failed', LogLevel.ERROR, LogCategory.GRAPH, { error: message, userId });
      return []; // Return empty array to prevent UI crash
    }
  }
}

const knowledgeGraph = new KnowledgeGraph();
knowledgeGraph.loadData(INITIAL_NODES, INITIAL_EDGES);

export { knowledgeGraph };
